import { useEffect, useState, type ReactNode } from 'react';
import { STRIP_SWAP_MS } from './RealmMap';

/**
 * The overview's host: the old overview or the strip, and the one swap between them that can happen while it is up. The first launch
 * after the strip goes on, a device builds the old overview (what it remembers) before the player's flags arrive; when they say the
 * strip, the strip draws hidden under it, then the old overview fades out over it (a cut, with reduced motion), so there is never a
 * blank frame or a hard swap. Every other case (the strip remembered, the flag off, the kill switch) renders exactly as the plain
 * `stripOn ? strip : old` it stands for.
 *
 * The old overview stays mounted through the swap (the same position among the children), so its scene is never torn down early.
 */
export function OverviewSwap({
  stripOn,
  old,
  strip,
}: {
  stripOn: boolean;
  /** The old overview; `fadeOut` once the strip has drawn. */
  old: (fadeOut: boolean) => ReactNode;
  /** The strip; `swapping` while it draws hidden under the old overview, and `drawn` is to be called when it has. */
  strip: (swap: { swapping: boolean; drawn: () => void }) => ReactNode;
}) {
  // 'up': the old overview is the overview. 'waiting' and 'fading': the strip has replaced it in place. 'none': the strip alone.
  const [phase, setPhase] = useState<'up' | 'waiting' | 'fading' | 'none'>(stripOn ? 'none' : 'up');
  // Adjusted in the render, so the old overview is not unmounted between the flags arriving and the swap starting.
  if (stripOn && phase === 'up') setPhase('waiting');
  else if (!stripOn && phase !== 'up') setPhase('up');

  useEffect(() => {
    if (phase !== 'fading') return undefined;
    const timer = setTimeout(() => setPhase('none'), STRIP_SWAP_MS + 40);
    return () => clearTimeout(timer);
  }, [phase]);

  const swapping = phase === 'waiting' || phase === 'fading';
  const drawn = (): void => {
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    setPhase((p) => (p === 'waiting' ? (still ? 'none' : 'fading') : p));
  };
  return (
    <>
      {stripOn ? strip({ swapping, drawn }) : null}
      {!stripOn || swapping ? old(phase === 'fading') : null}
    </>
  );
}
